import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, type NavigateOptions, type To } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useListPreferences, useSetPreference } from "@/queries/user-preferences-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

/**
 * useContextURL is a hook that provides a context URL from the hash of the current location
 *
 * @returns contextURL: the URL from the hash of the current location
 */
export function useContextURL(initialContextURL: string) {
    const location = useLocation();
    const navigate = useNavigate();

    const [contextURL, setContextURL] = useState<string | undefined>();
    const [contextURLValid, setContextURLValid] = useState<boolean>(false);

    const debouncedNavigate = useDebouncedCallback((to: To, options?: NavigateOptions) => {
        navigate(to, options);
    }, 1000);

    const updateContextURL = useCallback(
        (newUrl: string, fromLocation = false) => {
            setContextURL((prev) => {
                if (prev === newUrl) return prev;
                if (!fromLocation) {
                    //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    debouncedNavigate({ hash: newUrl }, { replace: true, state: { ...location.state, fromLocation } });
                }
                return newUrl;
            });
            try {
                setContextURLValid(new URL(newUrl.toLocaleLowerCase()).pathname.length > 3);
            } catch {
                setContextURLValid(false);
            }
        },
        [debouncedNavigate, location.state],
    );

    const isValidURL = useCallback((url: string): boolean => /^https?:\/\//.test(url), []);
    // Handle initial URL
    useEffect(() => {
        if (initialContextURL && isValidURL(initialContextURL)) {
            updateContextURL(initialContextURL, true);
        }
    }, [initialContextURL, isValidURL, updateContextURL]);
    useEffect(() => {
        const newUrl = (location.hash || "#").slice(1).trim();
        if (isValidURL(newUrl)) {
            updateContextURL(newUrl, true);
        }
    }, [location.hash, updateContextURL, isValidURL]);

    return {
        contextURL,
        /**
         * `setContext` must be a memoized function, i.e. a result of useCallback. Otherwise whenever `location.hash` changes
         * and the effect is running to sync the incoming change, it would notify the parent, which, because it's depending
         * on `setContextURL`, would re-render and effectively override the update.
         */
        setContextURL: updateContextURL,
        contextURLValid,
    };
}

const CONTEXT_URLS = "CONTEXT_URLS";

export function useListContextUrls() {
    const { preferences, isPending: isPendingPreferences } = useListPreferences();

    let contextUrls: string[] | undefined;
    const preferenceValue = preferences?.find((p) => p.key === CONTEXT_URLS)?.value;
    if (preferenceValue) {
        try {
            contextUrls = JSON.parse(preferenceValue) as string[];
        } catch (e) {
            console.error(`Failed to parse ${CONTEXT_URLS} preference`, e);
        }
    }

    return {
        contextUrls,
        isPending: isPendingPreferences,
    };
}

export function useSetContextUrls() {
    const { toast } = useToast();
    const setPreference = useSetPreference();

    return useCallback(
        async (contextUrls: string[]) => {
            try {
                let value = JSON.stringify(contextUrls.slice(0, 50));
                if (value.length >= 2480) {
                    // Guard against max length for user preference value defined in proto
                    value = JSON.stringify(contextUrls.slice(0, 30));
                }
                await setPreference.mutateAsync({ key: CONTEXT_URLS, value });
            } catch (e) {
                toast({
                    title: "Failed to update context urls",
                    description: formatError(e),
                });
            }
        },
        [setPreference, toast],
    );
}
